import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, UserPlus, Rocket, Archive, Check, X } from "lucide-react";

const STATUS_COLORS = {
  draft: "bg-amber-100 text-amber-700",
  active: "bg-emerald-100 text-emerald-700",
  archived: "bg-secondary text-muted-foreground",
};
const MEMBER_STATUS = {
  invited: "bg-amber-100 text-amber-700",
  accepted: "bg-emerald-100 text-emerald-700",
  declined: "bg-rose-100 text-rose-700",
};

export default function CollectiveDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const t = useT();
  usePageMeta(t("col2.detail_meta"));
  const [col, setCol] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState("");
  const [sharePct, setSharePct] = useState("20");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/collectives/${id}`); setCol(data); } catch { setCol(false); }
  }, [id]);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const isOwner = col?.my_role === "owner";

  const invite = async () => {
    if (!/@/.test(email)) { toast.error(t("col2.err_email")); return; }
    setBusy(true);
    try { await api.post(`/collectives/${id}/members/invite`, { coach_email: email.trim(), revenue_share_pct: Number(sharePct) || 0 }); toast.success(t("col2.invited")); setInviting(false); setEmail(""); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    setBusy(true);
    try { await api.patch(`/collectives/${id}`, { status }); toast.success(t("col2.saved")); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  const respond = async (accept) => {
    setBusy(true);
    try { await api.post(`/collectives/${id}/members/respond`, { accept }); toast.success(accept ? t("col2.resp_accepted") : t("col2.resp_declined")); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  if (col === false) return <Navigate to="/app/coach/collectives" replace />;

  return (
    <div data-testid="coach-collective-detail-page">
      <Link to="/app/coach/collectives" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="cold-back">
        <ChevronLeft className="h-4 w-4" /> {t("col2.title")}
      </Link>

      {col === null ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : (
        <div className="space-y-5">
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            {col.cover_image_url && <img src={fileUrl(col.cover_image_url)} alt="" className="h-40 w-full object-cover" />}
            <div className="p-5">
              <div className="flex items-center gap-2">
                <h1 className="font-display text-2xl text-foreground">{col.name}</h1>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLORS[col.status] || ""}`}>{t(`col2.status_${col.status}`)}</span>
              </div>
              {col.description && <p className="mt-1 text-sm text-muted-foreground">{col.description}</p>}
            </div>
          </div>

          {col.my_status === "invited" && (
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="cold-invite-respond">
              <p className="text-sm text-amber-800">{t("col2.you_invited")}</p>
              <div className="flex gap-2">
                <Button size="sm" className="gap-1 rounded-full" disabled={busy} onClick={() => respond(true)} data-testid="cold-accept"><Check className="h-3.5 w-3.5" /> {t("col2.accept")}</Button>
                <Button size="sm" variant="outline" className="gap-1 rounded-full text-rose-600" disabled={busy} onClick={() => respond(false)} data-testid="cold-decline"><X className="h-3.5 w-3.5" /> {t("col2.decline")}</Button>
              </div>
            </div>
          )}

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="font-display text-lg text-foreground">{t("col2.members")}</p>
              {isOwner && <Button size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={() => setInviting(true)} data-testid="cold-invite-btn"><UserPlus className="h-3.5 w-3.5" /> {t("col2.invite")}</Button>}
            </div>
            <div className="space-y-2">
              {(col.members || []).map((m) => {
                const name = m.coach_name || m.name || m.coach_id;
                const photo = m.coach_photo || m.avatar_url || m.photo;
                return (
                  <div key={m.coach_id} data-testid={`cold-member-${m.coach_id}`} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3">
                    {photo ? <img src={photo} alt="" className="h-9 w-9 rounded-full object-cover" /> : <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">{initials(name)}</span>}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{name}{m.role === "owner" ? ` · ${t("col2.owner")}` : ""}</p>
                      <p className="text-xs text-muted-foreground">{t("col2.share_pct", { pct: Math.round(m.revenue_share_pct || 0) })}</p>
                    </div>
                    {m.invitation_status && m.invitation_status !== "accepted" && <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${MEMBER_STATUS[m.invitation_status] || ""}`}>{t(`col2.mstatus_${m.invitation_status}`)}</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {isOwner && (
            <div className="flex flex-wrap gap-2">
              {col.status !== "active" && <Button className="gap-1.5 rounded-full" disabled={busy} onClick={() => setStatus("active")} data-testid="cold-publish"><Rocket className="h-4 w-4" /> {t("col2.publish")}</Button>}
              {col.status !== "archived" && <Button variant="outline" className="gap-1.5 rounded-full" disabled={busy} onClick={() => setStatus("archived")} data-testid="cold-archive"><Archive className="h-4 w-4" /> {t("col2.archive")}</Button>}
            </div>
          )}
        </div>
      )}

      {inviting && (
        <Dialog open onOpenChange={(o) => !o && setInviting(false)}>
          <DialogContent data-testid="cold-invite-dialog">
            <DialogHeader><DialogTitle>{t("col2.invite_title")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col2.invite_email")}</label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="coach@example.com" data-testid="cold-invite-email" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col2.invite_share")}</label>
                <Input type="number" value={sharePct} onChange={(e) => setSharePct(e.target.value)} className="max-w-[120px]" data-testid="cold-invite-share" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-full" onClick={() => setInviting(false)}>{t("common.cancel")}</Button>
              <Button className="rounded-full" onClick={invite} disabled={busy} data-testid="cold-invite-send">{busy ? t("common.saving") : t("col2.invite")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
